import { Button, Stack, Typography } from '@mui/material';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { useEventStore } from '@/state/stores/use-event-store';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { getObjectId } from '@/helpers/object.id.helper';
import {
  useArchiveScopedFlowsMutation,
  useCreateScopedPlanMutation,
  useRestoreScopedFlowsMutation,
} from '@/state/mutations/flows/use-scoped-flow';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useNotifications } from '@/hooks/use-notifications';

type ScopedFlowControlsProps = {
  eventSlotId?: string | null;
};

export default function ScopedFlowControls({ eventSlotId }: ScopedFlowControlsProps) {
  const t = useTranslations();
  const notification = useNotifications();
  const event = useEventStore((s) => s.event);
  const flow = useFlowStore((s) => s.flow);
  const { hasAccess: hasMasterFlow, isLoading } = useFeatureAccess('master-flow');
  const createScopedPlan = useCreateScopedPlanMutation();
  const archiveScoped = useArchiveScopedFlowsMutation();
  const restoreScoped = useRestoreScopedFlowsMutation();

  if (isLoading || !hasMasterFlow || !event?.multiFlow || !eventSlotId || !flow) {
    return null;
  }

  const masterPlanFlowId = flow.origin ? getObjectId(flow.origin) : flow.id;

  const run = async (action: 'create' | 'archive' | 'restore') => {
    try {
      if (action === 'create') {
        await createScopedPlan.mutateAsync({ flowId: masterPlanFlowId, eventSlotId });
        notification.notifySuccess(t('flows.detail.scopedFlow.createSuccess'));
      } else if (action === 'archive') {
        await archiveScoped.mutateAsync({ flowId: masterPlanFlowId, eventSlotId });
        notification.notifySuccess(t('flows.detail.scopedFlow.archiveSuccess'));
      } else {
        await restoreScoped.mutateAsync({ flowId: masterPlanFlowId, eventSlotId });
        notification.notifySuccess(t('flows.detail.scopedFlow.restoreSuccess'));
      }
    } catch {
      notification.notifyError(t('flows.detail.scopedFlow.error'));
    }
  };

  const pending = createScopedPlan.isPending || archiveScoped.isPending || restoreScoped.isPending;

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <Typography variant="subtitle2">{t('flows.detail.scopedFlow.title')}</Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="outlined" disabled={pending} onClick={() => run('create')}>
          {t('flows.detail.scopedFlow.createPlan')}
        </Button>
        <Button size="small" variant="outlined" color="warning" disabled={pending} onClick={() => run('archive')}>
          {t('flows.detail.scopedFlow.archive')}
        </Button>
        <Button size="small" variant="outlined" disabled={pending} onClick={() => run('restore')}>
          {t('flows.detail.scopedFlow.restore')}
        </Button>
      </Stack>
    </Stack>
  );
}
